// ==================== QUICK ACTIONS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, FileText, Users, MessageSquare, Bot, type LucideIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { staggerItem } from '../../../utils/animations';

// ==================== TYPES ====================

interface QuickAction {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  description?: string;
}

interface QuickActionsProps {
  className?: string;
}

// ==================== CONSTANTS ====================

const QUICK_ACTIONS: QuickAction[] = [
  { 
    id: 'new-campaign', 
    label: 'New Campaign', 
    icon: Send, 
    href: '/app/campaigns',
    description: 'Create a new message campaign',
  },
  { 
    id: 'new-template', 
    label: 'New Template', 
    icon: FileText, 
    href: '/app/templates',
    description: 'Create a WhatsApp template',
  },
  { 
    id: 'import-contacts', 
    label: 'Import Contacts', 
    icon: Users, 
    href: '/app/contacts',
    description: 'Add contacts from CSV',
  },
  { 
    id: 'send-message', 
    label: 'Send Message', 
    icon: MessageSquare, 
    href: '/app/messages',
    description: 'Send a quick message',
  },
  { 
    id: 'keyword-bots', 
    label: 'Keyword Bots', 
    icon: Bot, 
    href: '/app/keyword-bots',
    description: 'Manage auto-replies',
  },
];

// ==================== COMPONENT ====================

export const QuickActions = ({ className }: QuickActionsProps) => {
  return (
    <motion.div variants={staggerItem}>
      <Card className={className}>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="grid grid-cols-2 gap-2">
            {QUICK_ACTIONS.map((action) => (
              <Link key={action.id} to={action.href}>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="w-full justify-start hover:border-(--color-signal-orange)/50 transition-colors text-xs py-1.5"
                >
                  <action.icon size={14} />
                  <span className="truncate">{action.label}</span>
                </Button>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default QuickActions;
